import { inject } from '@angular/core';
import { CanMatchFn, Routes } from '@angular/router';
import { AuthGuard } from './core/auth/guards/auth.guard';
import { AppModeService } from './core/services/app-mode.service';
import { CongresoThemeService } from './features/congreso/services/congreso-theme.service';

const adminModeGuard: CanMatchFn = async () => {
   const appModeService = inject(AppModeService);

   // Esperamos hasta saber si estamos en modo admin o congreso (o desconocido) antes de permitir cualquier navegación.
   await appModeService.waitUntilResolved();

   return appModeService.isAdmin();
};

const congresoModeGuard: CanMatchFn = async () => {
   const appModeService = inject(AppModeService);
   // Instanciamos el theme del congreso al entrar en cualquier ruta /web (también tras un
   // refresh directo en una subruta): su effect aplica el branding del congreso (color)
   // globalmente en :root. Es exclusivo de modo congreso, no afecta al admin.
   inject(CongresoThemeService);

   // Esperamos hasta saber si estamos en modo admin o congreso (o desconocido) antes de permitir cualquier navegación.
   await appModeService.waitUntilResolved();

   return appModeService.isCongreso();
};

export const appRoutes: Routes = [
   {
      path: '',
      pathMatch: 'full',
      loadComponent: () => import('./core/layouts/root-entry/root-entry.component').then(m => m.RootEntryComponent)
   },

   // ============================================================================
   // ESPACIO PARA RUTAS PÚBLICAS PERSONALIZADAS (sin autenticación)
   // ============================================================================
   // Ejemplo de ruta pública:
   // {
   //    path: 'public/mi-feature/:param',
   //    loadComponent: () => import('./features/mi-feature/mi-feature.component')
   //       .then(m => m.MiFeatureComponent),
   //    data: {
   //       titulo: 'Mi Feature Pública',
   //       showSidebar: false
   //    }
   // },

   // ============================================================================
   // RUTAS PRIVADAS (requieren autenticación)
   // ============================================================================
   {
      path: 'app',
      canMatch: [adminModeGuard],
      canActivate: [AuthGuard],
      loadChildren: () => import('./features/admin/admin.routes').then(m => m.adminRoutes)
   },
   {
      path: 'web',
      canMatch: [congresoModeGuard],
      loadChildren: () => import('./features/congreso/congreso.routes').then(m => m.congresoRoutes)
   },
   {
      path: 'error',
      loadChildren: () => import('./core/errors/errors.routes').then(m => m.errorsRoutes)
   },
   {
      path: 'auth',
      canMatch: [adminModeGuard],
      loadChildren: () => import('./core/auth/auth.routes').then(m => m.authRoutes)
   },
   // Entrada de Secretaría / administración de congreso: `<host-congreso>/admin`.
   // El initializer ya ha activado el modo admin con el congreso resuelto por URL
   // (ver app-mode.initializer.ts); aquí solo se lleva a la pantalla de login de admin.
   {
      path: 'admin',
      canMatch: [adminModeGuard],
      redirectTo: '/auth/login',
      pathMatch: 'full'
   },
   { path: '**', redirectTo: '/error/404' }
];
